import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService, FilterService } from '../../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceCostingService } from '../../../services/maintenance-costing.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { SummaryRequest } from '../../../../../../_shared/models/summary-request.model';
import { hasCompleteDateRange, isSameWidgetFilter } from '../../../../../../_shared/helpers/widget-filter.helper';
import { MaintenanceInsightsResponse } from '../../../../maintenance-dashboard/models/maintenance-dashboard-response.model';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { isEmptyObject } from '../../../../../../_shared/helpers/object.helper';
import { MultiLineChartComponent, MultiLineChartDataPoint, MultiLineChartSeries, LegendItem } from '../../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { formatValue } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { CpkGrouping, groupAverageCpk } from '../../../helpers/cpk-grouping.helper';
import { CPK_DEFAULT_Y_TICKS, CPK_TICK_DECIMALS, CPK_TICK_INTERVALS } from '../../../constants/cpk-chart.constants';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-cpk-trend-modal',
  standalone: true,
  imports: [CommonModule, MultiLineChartComponent, EmptyStateComponent, GraphTrendIconComponent, AccountFilterComponent, DateFilterComponent, TranslocoPipe],
  templateUrl: './cpk-trend-modal.component.html',
  styleUrl: './cpk-trend-modal.component.scss'
})
export class CpkTrendModalComponent implements OnInit, OnDestroy {
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private costingService = inject(MaintenanceCostingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);
  private widgetFilter: WidgetFilterData = { ...this.filterService.filter };

  // Every day of a month-long range fits in the modal; longer ranges are grouped into weeks
  private readonly maxDailyRangeDays = 31;

  chartData: MultiLineChartDataPoint[] = [];
  private readonly defaultYTicks = CPK_DEFAULT_Y_TICKS;
  yTicks: number[] = this.defaultYTicks;
  readonly chartHeight = 335;

  readonly series: MultiLineChartSeries[] = [
    { key: 'inHouse', color: '#8D63C3', label: this.transloco.translate('maintenanceCosting.charts.cpkTrend.legend.inHouse') },
    { key: 'outsourced', color: '#F4BC2B', label: this.transloco.translate('maintenanceCosting.charts.cpkTrend.legend.outsourced') }
  ];

  readonly legendItems: LegendItem[] = [
    { legend: this.transloco.translate('maintenanceCosting.charts.cpkTrend.legend.inHouse'), color: '#8D63C3' },
    { legend: this.transloco.translate('maintenanceCosting.charts.cpkTrend.legend.outsourced'), color: '#F4BC2B' }
  ];

  readonly yAxisFormatter = (value: any): string => {
    return formatValue(Number(value), { currencySymbol: this.currencySymbol });
  };

  isLoading: boolean = true;
  // Set by the opening card to the page's current selection
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  totalValue: number = 0;
  percentageChange: number = 0;
  currencySymbol: string = '';
  currencyCode: string = '';

  private cancelSubject: Subject<void> = new Subject<void>();

  get title(): string {
    return this.transloco.translate('maintenanceCosting.charts.cpkTrend.title');
  }

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  async ngOnInit(): Promise<void> {
    await this.loadCpkData();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (isEmptyObject(this.dateFilter) || isEmptyObject(this.accountFilter)) {
      return;
    }

    const filter = {
      ...this.dateFilter,
      accountIds: [...this.accountFilter.accountIds]
    } as WidgetFilterData;

    // The account filter re-emits the selection the modal opened with once its accounts load
    if (isSameWidgetFilter(filter, this.widgetFilter)) {
      return;
    }

    this.widgetFilter = filter;
    await this.loadCpkData();
  }

  async loadCpkData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: SummaryRequest = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const [inHouseResponse, outsourcedResponse] = await Promise.all([
        this.costingService.InHouseCostPerKm(request, this.cancelSubject),
        this.costingService.OutSourceCostPerKm(request, this.cancelSubject)
      ]);

      this.chartData = this.processCpkData(inHouseResponse.data?.data ?? [], outsourcedResponse.data?.data ?? []);
      this.totalValue = inHouseResponse.data?.totalValue ?? 0;
      this.percentageChange = inHouseResponse.data?.percentageChange ?? 0;
      this.calculateYTicks(this.chartData);

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processCpkData(
    inHouseData: MaintenanceInsightsResponse[],
    outsourcedData: MaintenanceInsightsResponse[]
  ): MultiLineChartDataPoint[] {
    if (inHouseData.length === 0 && outsourcedData.length === 0) return [];

    const { startDate, endDate } = this.widgetFilter;
    const daysDifference = differenceInDays(endDate!, startDate!);

    const averaged = groupAverageCpk([
      { label: 'inHouse', data: inHouseData },
      { label: 'outsourced', data: outsourcedData }
    ], this.determineGroupingType(daysDifference), startDate!, endDate!);

    return averaged.map(item => ({
      x: item.label,
      inHouse: item['inHouse'],
      outsourced: item['outsourced']
    }));
  }

  private determineGroupingType(daysDifference: number): CpkGrouping {
    if (daysDifference === 0) return 'individualHours';
    if (daysDifference <= this.maxDailyRangeDays) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(data: MultiLineChartDataPoint[]): void {
    const allValues = data.flatMap(point => this.series
      .map(s => point[s.key])
      .filter((value): value is number => typeof value === 'number'));

    this.yTicks = calculateChartYTicks(allValues, this.defaultYTicks, CPK_TICK_INTERVALS, CPK_TICK_DECIMALS);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false; // CPK decrease is positive (lower cost per km)
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  formatTooltipValue = (value: number): string => {
    return `${this.currencySymbol}${value.toLocaleString('default', { maximumFractionDigits: 2 })}`;
  };

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return this.series.some(s => {
        const value = Number(item[s.key]) || 0;
        return value > 0;
      });
    });
  }

  close(): void {
    this.activeModal.close();
  }
}
